/* eslint-disable @typescript-eslint/naming-convention */
import { useLocationPathname } from '@rocketlane/rocket-router';
import { Link } from '@rocketlane/rocket-router';
import { FC, forwardRef, ComponentClass } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Breadcrumb as StyledBreadcrumb } from 'components/atoms/breadcrumb';
import { ClassNamePropsWithChildren } from 'components/ozone-component';

const BreadcrumbWrapper = styled.div`
  a:focus-visible {
    outline: solid 1px;
  }
`;

export interface BreadcrumbItemProps {
  url?: string;
  label: string | undefined;
}

type StaticBreadcrumbProps = {
  items?: BreadcrumbItemProps[];
};

export type BreadcrumbProps = ClassNamePropsWithChildren &
  StaticBreadcrumbProps;

type BreadcrumbType = ComponentClass<BreadcrumbProps>;

/**
 * Breadcrumb map - which we can potentially consume from a routes.ts file in future
 * that contains all the segments, routes and its invocations to the route components
 */
const BREADCRUMBS_MAP = {
  '/projects': 'projects',
  '/projects/:projectId': 'details',
  '/projects/:projectId/plan': 'plan',
};

/**
 * DynamicBreadcrumb component to render the breadcrumb items based on the
 * url route match and the location path.
 * Figures out if there is a route map specified for a match.path and uses that for
 * translation. If not, it picks up from the direct translation key of that path and uses
 * to translate the file. If no translation is found, the default formatMessage fallback is
 * used. The idea is to keep breadcrumb from being defined as minimal and as generic as possible.
 * And the idea of using translation for rendering the text is to make sure all the
 * text in the product is translated.
 */
const DynamicBreadcrumb = () => {
  const intl = useIntl();
  const path = useLocationPathname();
  const pathSnippets = path.split('/').filter((i) => i);
  const totalpaths = pathSnippets.length - 1;
  const urlSnippets = path.split('/').filter((i) => i);
  const extraBreadcrumbItems = pathSnippets.map((crumb, index) => {
    const url = `/${urlSnippets.slice(0, index + 1).join('/')}`;
    const pathKey = `/${pathSnippets.slice(0, index + 1).join('/')}`;
    const breadcrumbKey = BREADCRUMBS_MAP[pathKey] || crumb;
    const label = intl.formatMessage({ id: `breadcrumbs.${breadcrumbKey}` });

    return (
      <StyledBreadcrumb.Item key={url}>
        {index === totalpaths ? label : <Link to={url}>{label}</Link>}
      </StyledBreadcrumb.Item>
    );
  });
  const breadcrumbItems = [<StyledBreadcrumb.Item key="home" />].concat(
    extraBreadcrumbItems,
  );

  return <StyledBreadcrumb>{breadcrumbItems}</StyledBreadcrumb>;
};

/**
 * StaticBreadcrumb is used for customised breadcrumbs where in the consumer,
 * supplies the items: [] from their respective templates and expect that to be
 * rendered. More effective in places like `/projects/:projectId/<whatever>` path
 * based breadcrumbs where the `:projectId` might be useed to show the project name
 * instead of `Details`.
 * @param items: BreadcrumbItemProps
 */
const StaticBreadrumb: FC<React.PropsWithChildren<StaticBreadcrumbProps>> = ({
  items = [],
}) => (
  <StyledBreadcrumb>
    {items.map((item) => (
      <StyledBreadcrumb.Item key={`${item.url}${item.label}`}>
        {item.url ? <Link to={item.url}>{item.label}</Link> : item.label}
      </StyledBreadcrumb.Item>
    ))}
  </StyledBreadcrumb>
);

/**
 * Breadcrumb shell component that determines whether a dynamic breadcrumb or a supplied
 * breadcrumb items component needs to be rendered.
 */
// eslint-disable-next-line react/display-name
export const Breadcrumb: BreadcrumbType = forwardRef(
  (props: BreadcrumbProps, ref) => {
    const { children, className, items, ...rest } = props;

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <BreadcrumbWrapper ref={ref as any} className={className} {...rest}>
        {items && Boolean(items.length > 0) ? (
          <StaticBreadrumb items={items} />
        ) : (
          <DynamicBreadcrumb />
        )}
      </BreadcrumbWrapper>
    );
  },
) as unknown as BreadcrumbType;
